import { useEffect, useMemo } from 'react';
import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';
import { onPopstateCaptured } from 'quartz-routing/internal/popstate';
import { syncHistoryToBrowser } from 'quartz-routing/initialize';
let activeRemote = null;
export function _deactivateActiveRemote() {
  if (activeRemote) {
    activeRemote.historyUnlisten();
    activeRemote = null;
  }
}
export function _createAttachHistory(remoteModule, browserHistory) {
  return (remoteHistory, basename) => {
    // Host deactivates the previous remote immediately — the remote has no
    // control over when the host stops listening.
    _deactivateActiveRemote();

    // remoteHistory is always a GraniteRemoteHistory created by
    // initializeRemoteHistory — the cast is safe.
    const historyUnlisten = syncHistoryToBrowser(remoteHistory, browserHistory);
    activeRemote = {
      history: remoteHistory,
      historyUnlisten,
      remoteModule,
      basename
    };
  };
}
export function _getActiveRemote() {
  return activeRemote;
}
export function _resetForTesting() {
  _deactivateActiveRemote();
}
export function useAttachGraniteRemoteHistory(remoteModule) {
  return useMemo(() => _createAttachHistory(remoteModule), [remoteModule]);
}

/**
 * Syncs the active remote's GraniteRemoteHistory location on browser
 * Back/Forward events.
 *
 * Scenario: the user navigates Back between sub-pages within the same remote
 * (e.g. Back between record sub-routes in crm-records-ui). The browser URL
 * changes via the DOM history API, but GraniteRemoteHistory has no popstate
 * listener — nothing would update the remote's React Router location and the
 * remote would be stuck showing the previous sub-page. This hook intercepts
 * popstate in capture phase and calls history.replace() on the active remote so
 * its router re-renders to the correct sub-page before the host Switch
 * re-renders.
 *
 * For cross-remote navigation the host's createBrowserHistory update causes the
 * Switch to unmount/mount the right remote; no remote history sync is needed.
 */
export function usePopstateCapture(history, localRouteOverrides) {
  useEffect(() => {
    const handler = () => {
      const newPathname = history.location.pathname;
      const matchedKey = findMostSpecificRoute(Object.keys(localRouteOverrides), newPathname);
      if (!matchedKey) return;
      const newRemoteModule = localRouteOverrides[matchedKey].remoteModule;
      const active = _getActiveRemote();
      if (newRemoteModule && active && active.remoteModule === newRemoteModule) {
        const relativePath = (history.location.pathname + (history.location.search || '')).slice(active.basename.length) || '/';
        active.history.replace(relativePath);
      }
    };
    return onPopstateCaptured(handler);
  }, [history, localRouteOverrides]);
}